/**
 * Phase 7.5b · `.drawer` disclosure handler.
 * Ported from `01_BUILD/qa_studio.html` L22773 `initDrawers()`.
 *
 * Different from `.senior-extension` which uses native `<details>` element ·
 * `.drawer` is class-toggle based (no native semantics) · click `.drawer-head`
 * toggles `.open` on parent which CSS uses to expand/collapse `.drawer-body`
 * via `max-height` transition.
 *
 * Markup contract:
 *   <div class='drawer'>
 *     <div class='drawer-head'>Toggle label · ▾</div>
 *     <div class='drawer-body'>...content...</div>
 *   </div>
 *
 * Gating: `.drawer-body { max-height: 0 }` by default ·
 * `.drawer.open .drawer-body { max-height: 1400px }` on toggle.
 *
 * State: session-only (no persistence · users re-open as needed).
 */
export function initDrawers(root: HTMLElement): () => void {
  const drawers = Array.from(root.querySelectorAll<HTMLElement>('.drawer'));
  const cleanups: Array<() => void> = [];

  for (const drawer of drawers) {
    if (drawer.dataset.drawerInit === '1') continue;
    drawer.dataset.drawerInit = '1';
    const head = drawer.querySelector<HTMLElement>('.drawer-head');
    if (!head) continue;

    // Make head keyboard-focusable + ARIA-toggleable.
    if (!head.hasAttribute('tabindex')) head.tabIndex = 0;
    if (!head.hasAttribute('role')) head.setAttribute('role', 'button');
    head.setAttribute('aria-expanded', drawer.classList.contains('open') ? 'true' : 'false');

    const toggle = () => {
      const opened = drawer.classList.toggle('open');
      head.setAttribute('aria-expanded', opened ? 'true' : 'false');
    };

    const onClick = () => toggle();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    };

    head.addEventListener('click', onClick);
    head.addEventListener('keydown', onKey);
    cleanups.push(() => {
      head.removeEventListener('click', onClick);
      head.removeEventListener('keydown', onKey);
      delete drawer.dataset.drawerInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
